import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Package, Truck, AlertTriangle, FileText, Users, BarChart3,
  RefreshCw, ShieldAlert,
} from "lucide-react";
import axios from "axios";
import useSeo from "../hooks/useSeo";

const API = process.env.REACT_APP_BACKEND_URL;

// Tab definitions — only shown if the employee has the matching permission
const TABS = [
  { id: "shipments",    label: "Forsendelser",  icon: Package,       perm: "shipments.read" },
  { id: "routes",       label: "Ruter",          icon: Truck,         perm: "routes.read" },
  { id: "alerts",       label: "Alerts",         icon: AlertTriangle, perm: "alerts.read" },
  { id: "invoice",      label: "Faktura-grundlag", icon: FileText,    perm: "invoice_basis.read" },
  { id: "settlements",  label: "Vognmand-afregning", icon: FileText,  perm: "sc_settlement.read" },
  { id: "finance",      label: "Finance overview", icon: BarChart3,   perm: "finance_overview.read" },
  { id: "customers",    label: "Kunder",         icon: Users,         perm: "customers.read" },
];

function hasPerm(perms, perm) {
  return Array.isArray(perms) && (perms.includes("*") || perms.includes(perm));
}

export default function EmployeeDashboardPage() {
  useSeo({
    title: "Medarbejder dashboard · EMA Kurer",
    description: "Internt medarbejder dashboard.",
    path: "/employee",
  });
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState(null);
  const [summary, setSummary] = useState({});
  const [tabData, setTabData] = useState(null);
  const [loadingTab, setLoadingTab] = useState(false);
  const token = localStorage.getItem("ema_employee_token");

  const authHeader = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const logout = () => {
    localStorage.removeItem("ema_employee_token");
    localStorage.removeItem("ema_employee");
    navigate("/employee/login");
  };

  useEffect(() => {
    if (!token) {
      navigate("/employee/login");
      return;
    }
    (async () => {
      try {
        const meR = await axios.get(`${API}/api/employee/me`, authHeader());
        setMe(meR.data);
        if (meR.data.must_change_password) {
          navigate("/employee/skift-password");
          return;
        }
        const dashR = await axios.get(`${API}/api/employee/dashboard`, authHeader());
        setSummary(dashR.data || {});
        const firstTab = TABS.find((t) => hasPerm(meR.data.permissions, t.perm));
        if (firstTab) setTab(firstTab.id);
      } catch {
        toast.error("Session udløbet — log venligst ind igen.");
        logout();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!me || !tab) return;
    setLoadingTab(true);
    setTabData(null);
    const endpoints = {
      shipments: "/api/employee/shipments",
      routes: "/api/employee/routes",
      alerts: "/api/employee/alerts",
      invoice: "/api/employee/invoice-basis",
      settlements: "/api/employee/sc-settlements",
      finance: "/api/employee/finance-overview",
      customers: "/api/employee/customers",
    };
    axios
      .get(`${API}${endpoints[tab]}`, authHeader())
      .then((r) => setTabData(r.data))
      .catch((e) => {
        const msg = e?.response?.data?.detail || "Kunne ikke hente data";
        toast.error(msg);
        setTabData({ error: msg });
      })
      .finally(() => setLoadingTab(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, me]);

  if (!me) {
    return (
      <div className="min-h-screen bg-brand-dark text-white flex items-center justify-center">
        <div className="text-white/60">Indlæser...</div>
      </div>
    );
  }

  const ROLE_LABELS = {
    owner_admin: "Owner / Admin",
    admin: "Admin",
    operations_employee: "Operations",
    finance_employee: "Finance",
    support_employee: "Support",
  };
  const allowedTabs = TABS.filter((t) => hasPerm(me.permissions, t.perm));

  return (
    <div className="min-h-screen bg-brand-bg2" data-testid="employee-dashboard">
      {/* Header */}
      <div className="bg-brand-dark text-white px-5 sm:px-8 lg:px-12 py-5 flex items-center justify-between">
        <div>
          <div className="text-[10px] font-bold tracking-widest uppercase text-brand-red flex items-center gap-2">
            <ShieldAlert size={11}/> Medarbejder · {ROLE_LABELS[me.role] || me.role}
          </div>
          <div className="font-display font-bold text-xl mt-1">{me.name || me.email}</div>
        </div>
        <button onClick={logout} data-testid="employee-logout" className="text-xs flex items-center gap-2 text-white/80 hover:text-white border border-white/20 px-3 py-2">
          <LogOut size={14}/> Log ud
        </button>
      </div>

      {/* KPI summary */}
      <div className="px-5 sm:px-8 lg:px-12 py-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="employee-kpi-grid">
        {summary.shipments_today != null && (
          <div className="bg-white border border-brand p-4"><div className="text-xs uppercase text-brand-text2">Forsendelser i dag</div><div className="font-display font-bold text-2xl mt-1">{summary.shipments_today}</div></div>
        )}
        {summary.routes_active != null && (
          <div className="bg-white border border-brand p-4"><div className="text-xs uppercase text-brand-text2">Aktive ruter</div><div className="font-display font-bold text-2xl mt-1">{summary.routes_active}</div></div>
        )}
        {summary.failed_pending != null && (
          <div className="bg-white border border-brand p-4"><div className="text-xs uppercase text-brand-text2">Failed delivery</div><div className="font-display font-bold text-2xl mt-1 text-amber-700">{summary.failed_pending}</div></div>
        )}
        {summary.alerts_open != null && (
          <div className="bg-white border border-brand p-4"><div className="text-xs uppercase text-brand-text2">Åbne alerts</div><div className="font-display font-bold text-2xl mt-1 text-brand-red">{summary.alerts_open}</div></div>
        )}
        {summary.invoice_drafts != null && (
          <div className="bg-white border border-brand p-4"><div className="text-xs uppercase text-brand-text2">Faktura-drafts</div><div className="font-display font-bold text-2xl mt-1">{summary.invoice_drafts}</div></div>
        )}
        {summary.sc_settlements_pending != null && (
          <div className="bg-white border border-brand p-4"><div className="text-xs uppercase text-brand-text2">SC-afregninger</div><div className="font-display font-bold text-2xl mt-1">{summary.sc_settlements_pending}</div></div>
        )}
      </div>

      {/* Tabs */}
      <div className="px-5 sm:px-8 lg:px-12">
        <div className="bg-white border border-brand">
          <div className="border-b border-brand flex overflow-x-auto" data-testid="employee-tabs">
            {allowedTabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`employee-tab-${t.id}`}
                className={`px-4 py-3 text-sm whitespace-nowrap flex items-center gap-2 border-b-2 transition ${tab === t.id ? "border-brand-red text-brand-dark font-bold" : "border-transparent text-brand-text2 hover:text-brand-dark"}`}
              >
                <t.icon size={14}/> {t.label}
              </button>
            ))}
            {allowedTabs.length === 0 && (
              <div className="p-6 text-brand-text2 text-sm">Ingen tilladelser for din rolle. Kontakt din admin.</div>
            )}
          </div>

          <div className="p-5">
            {loadingTab && <div className="text-brand-text2 text-sm flex items-center gap-2"><RefreshCw size={14} className="animate-spin"/> Henter...</div>}
            {!loadingTab && tabData && tabData.error && (
              <div className="text-red-700 text-sm">{tabData.error}</div>
            )}
            {!loadingTab && tabData && !tabData.error && tab === "finance" && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="finance-data">
                <Card label="Omsætning ekskl. moms" value={`${(tabData.revenue_excl_vat || 0).toLocaleString("da-DK")} kr`} />
                <Card label="Vognmand-omkostning ekskl. moms" value={`${(tabData.cost_excl_vat || 0).toLocaleString("da-DK")} kr`} />
                <Card label="Margin ekskl. moms" value={`${(tabData.margin_excl_vat || 0).toLocaleString("da-DK")} kr`} highlight />
              </div>
            )}
            {!loadingTab && tabData && tabData.items != null && (
              <div className="overflow-x-auto" data-testid={`employee-table-${tab}`}>
                <table className="w-full text-sm">
                  <thead className="bg-brand-bg2 text-brand-text2 text-xs uppercase tracking-wider">
                    <tr>{Object.keys(tabData.items[0] || {}).slice(0, 8).map((k) => <th key={k} className="text-left p-2">{k}</th>)}</tr>
                  </thead>
                  <tbody>
                    {tabData.items.slice(0, 50).map((row, idx) => (
                      <tr key={row.id || idx} className="border-b border-brand">
                        {Object.keys(tabData.items[0] || {}).slice(0, 8).map((k) => (
                          <td key={k} className="p-2 text-brand-dark">{typeof row[k] === "object" ? JSON.stringify(row[k]).slice(0, 40) : (row[k] != null ? String(row[k]).slice(0, 80) : "—")}</td>
                        ))}
                      </tr>
                    ))}
                    {tabData.items.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-brand-text2">Ingen data</td></tr>}
                  </tbody>
                </table>
                {tabData.items.length > 50 && <div className="text-xs text-brand-text2 mt-2">Viser første 50 af {tabData.items.length} — kontakt admin for fuld eksport.</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-5 sm:px-8 lg:px-12 py-8 text-xs text-brand-text2">
        EMA Kurer ApS · CVR 46465717 · Internt system — kontakt {" "}
        <a href="mailto:kontakt@emakurer.dk" className="text-brand-red hover:underline">kontakt@emakurer.dk</a>
      </div>
    </div>
  );
}

function Card({ label, value, highlight }) {
  return (
    <div className={`p-4 border ${highlight ? "bg-brand-red text-white border-brand-red" : "bg-white border-brand"}`}>
      <div className={`text-xs uppercase ${highlight ? "text-white/80" : "text-brand-text2"}`}>{label}</div>
      <div className="font-display font-bold text-2xl mt-1">{value}</div>
    </div>
  );
}
